// Color variables for the entire application
// Updated to match the new theme: dark elements use #4C5BAD, buttons use #233491

export const colors = {
  // Primary/Accent Colors - Indigo/blue theme
  primary: '#233491', // Core button/interactive color
  primaryHover: '#1f2f7d', // Darker blue for hover states
  primaryLight: '#3f52b5', // Lighter blue
  primaryLighter: '#5568c7', // Very light blue
  primarySoft: '#e8ebf8', // Soft blue-tinted background
  primaryDark: '#4C5BAD', // Dark tone for sidebar and other dark elements
  primaryGradient: 'linear-gradient(90deg, #233491, #2f4ac4)', // Blue gradient
  primaryGradientHover: 'linear-gradient(90deg, #3a5cd0, #233491)', // Lighter blue gradient
  primaryGradientVertical: 'linear-gradient(135deg, #233491, #2f4ac4)', // Vertical blue gradient
  primaryShadow: 'rgba(35, 52, 145, 0.3)', // Blue shadow
  primaryShadowHover: 'rgba(35, 52, 145, 0.4)', // Blue shadow on hover
  primaryTint: 'rgba(35, 52, 145, 0.15)', // Blue tint
  primaryTintHover: 'rgba(35, 52, 145, 0.2)', // Blue tint on hover
  
  // Secondary Colors
  secondary: '#1e293b', // Dark slate for text on active items
  secondaryHover: '#334155', // Lighter slate for hover
  
  // Accent Colors - align with primary palette
  accent: '#2f4ac4',
  accentLight: '#3a5cd0',
  
  // Background Colors - Clean, neutral tones
  background: '#FAFAFA', // Very light neutral background
  backgroundWhite: '#FFFFFF',
  backgroundLight: '#F8F9FA',
  backgroundSoft: '#F5F5F5', // Soft gray for subtle backgrounds
  sidebarBackground: '#4C5BAD', // Updated dark tone for sidebar background
  
  // Text Colors - Refined neutral tones
  textPrimary: '#1e293b', // Dark slate for headers and primary text
  textPrimaryDark: '#4C5BAD', // Updated dark tone for emphasis/table headers
  textSecondary: '#6B7280', // Medium neutral gray for secondary text
  textTertiary: '#9CA3AF', // Light neutral gray for tertiary text
  textWhite: '#FFFFFF',
  textWhiteMuted: 'rgba(255, 255, 255, 0.7)', // Muted white for sidebar inactive items
  textWhiteSemiMuted: 'rgba(255, 255, 255, 0.85)', // Semi-muted white
  textWhiteBright: 'rgba(255, 255, 255, 0.9)', // Bright white for hover
  textDisabled: '#D1D5DB',
  
  // Header Colors - Dark theme
  headerColor: '#4C5BAD', // Updated header color
  headerColorHover: '#3f4f9a', // Slightly lighter for hover states
  
  // Border Colors - Soft, subtle borders
  borderDefault: '#E5E7EB', // Soft neutral border
  borderLight: '#F3F4F6', // Very light border
  borderHover: '#D1D5DB', // Slightly darker on hover
  borderFocus: '#233491', // Primary focus ring
  borderSoft: '#E8E9EB', // Extra soft border
  borderSidebar: 'rgba(255, 255, 255, 0.1)', // Sidebar border (white with opacity)
  
  // Status Colors - Updated to match new theme
  success: '#10B981', // Green for success/positive
  successLight: '#34D399',
  warning: '#F59E0B', // Amber for warnings
  warningLight: '#FCD34D',
  error: '#EF4444', // Red for errors
  errorLight: '#F87171',
  info: '#3B82F6', // Blue for info
  infoLight: '#60A5FA',
  
  // Card Colors - Soft, elegant
  cardBackground: '#FFFFFF',
  cardBorder: '#E5E7EB',
  cardShadow: 'rgba(0, 0, 0, 0.04)',
  cardShadowSoft: 'rgba(0, 0, 0, 0.02)',
  cardShadowHover: 'rgba(0, 0, 0, 0.08)',
  
  // Button Colors - Indigo/blue gradient theme
  buttonPrimary: 'linear-gradient(90deg, #233491, #2f4ac4)',
  buttonPrimaryHover: 'linear-gradient(90deg, #3a5cd0, #233491)',
  buttonPrimaryText: '#FFFFFF', // Bright text on blue buttons
  buttonSecondary: '#F3F4F6',
  buttonSecondaryHover: '#E5E7EB',
  buttonSecondaryText: '#1F2937',
  buttonOutline: '#E5E7EB',
  buttonOutlineHover: '#D1D5DB',
  
  // Auth Button Colors - Indigo/blue gradient theme
  authButton: 'linear-gradient(90deg, #233491, #2f4ac4)',
  authButtonHover: 'linear-gradient(90deg, #3a5cd0, #233491)',
  authButtonDisabled: '#808080',
  
  // Chart/Graph Colors
  chartOrange: '#F59E0B',
  chartGreen: '#10B981',
  chartBlue: '#3B82F6',
  chartPurple: '#8B5CF6',
  chartYellow: '#3a5cd0',
  
  // Gradient Colors - Indigo/blue gradient theme
  gradientStart: '#233491',
  gradientEnd: '#2f4ac4',
  gradientSoft: 'linear-gradient(135deg, #e8ebf8 0%, #d7ddf3 50%, #e8ebf8 100%)',
  
  // Auth Gradient Colors - Indigo/blue theme
  authGradientStart: '#233491', // Blue
  authGradientEnd: '#2f4ac4', // Lighter blue
  authGradientSoft: 'linear-gradient(135deg, #233491 0%, #2f4ac4 100%)',
  
  // Sidebar Specific Colors
  sidebarShadow: 'rgba(76, 91, 173, 0.35)', // Updated dark shadow
  sidebarShadowAccent: 'rgba(76, 91, 173, 0.25)', // Accent shadow aligned to new dark tone
  sidebarActiveBackground: 'linear-gradient(90deg, #233491, #2f4ac4)',
  sidebarActiveText: '#FFFFFF',
  sidebarHoverBackground: 'rgba(255, 255, 255, 0.05)',
  sidebarInactiveText: 'rgba(255, 255, 255, 0.7)',
  sidebarSectionTitle: 'rgba(255, 255, 255, 0.6)',
  
  // Avatar/User Profile Colors
  avatarGradient: 'linear-gradient(135deg, #233491, #2f4ac4)',
  avatarText: '#FFFFFF',
  avatarShadow: 'rgba(35, 52, 145, 0.3)',
  
  // Logo Colors
  logoBackground: 'linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)',
  logoShadow: 'rgba(255, 255, 255, 0.2)',
  
  // Overlay Colors - Soft overlays
  overlay: 'rgba(0, 0, 0, 0.4)',
  overlayLight: 'rgba(0, 0, 0, 0.05)',
  overlayWhite: 'rgba(255, 255, 255, 0.8)',
  overlaySoft: 'rgba(255, 255, 255, 0.95)',
  overlayDark: 'rgba(14, 23, 42, 0.15)', // Dark overlay with primaryDark
  overlayDarkLight: 'rgba(14, 23, 42, 0.10)',
  overlayDarkMedium: 'rgba(14, 23, 42, 0.20)',
  
  // Glass Effect Colors - Frosted glass effect
  glassBackground: 'rgba(0, 0, 0, 0.08)',
  glassBackgroundHover: 'rgba(0, 0, 0, 0.12)',
  glassBorder: 'rgba(0, 0, 0, 0.1)',
  glassBorderLight: 'rgba(255, 255, 255, 0.8)',
};
